import { BooleanInput } from '@angular/cdk/coercion';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { RemoteConfigState } from '@app/core/firebase/remote-config/remote-config.state';
import { RemoteConfigKeys } from '@app/core/firebase/remote-config/remote-config.types';
import { ROUTE_NAMES } from '@app/core/navigation/navigation.constants';
import { TranslocoMissingHandlerController } from '@app/core/transloco/transloco-module.loader';
import { WebsocketService } from '@app/core/websocket/websocket.service';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { Language, Preference, Profile } from '@app/layout/common/profile/state/profile.types';
import { AvailableLangs, LangDefinition, TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { FindFirstAttachmentByTypeIdPipe } from '@shared/pipes/find-attachment-by-type-id.pipe';
import { Scheme, SealConfig, SealConfigService } from '@shipsfocus/seal';
import { UserActions } from '@user/state/user.actions';
import { UserState } from '@user/state/user.state';
import { SecureImagePipe } from 'app/core/pipe/secure-image.pipe';
import { environment } from 'environments/environment';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'profile',
  templateUrl: './profile.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'profile',
  imports: [RouterLink, TranslocoModule, MatButtonModule, MatIconModule, MatTooltipModule, SecureImagePipe, FindFirstAttachmentByTypeIdPipe, NgTemplateOutlet]
})
export class ProfileComponent implements OnDestroy, OnInit {
  static ngAcceptInputType_showAvatar: BooleanInput;
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  @Input() showAvatar: boolean = true;
  profile: Signal<Profile> = computed(() => this._profileState.profile());
  languageOpen: WritableSignal<boolean> = signal(false);
  fullScreenEnabled: WritableSignal<boolean> = signal(!!document.fullscreenElement);
  protected readonly environment: typeof environment = environment;
  protected readonly remoteConfigState: RemoteConfigState = inject(RemoteConfigState);
  protected readonly remoteConfig: Signal<RemoteConfigKeys> = computed(() => this.remoteConfigState.remoteConfig());
  protected readonly _userState: UserState = inject(UserState);
  protected readonly _userActions: UserActions = inject(UserActions);
  protected readonly translocoController: TranslocoMissingHandlerController = inject(TranslocoMissingHandlerController);
  private readonly _websocketService: WebsocketService = inject(WebsocketService);

  @ViewChild('profileOrigin') private _profileOrigin: MatButton;
  @ViewChild('profilePanel') private _profilePanel: TemplateRef<any>;

  private _overlayRef: OverlayRef;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  protected readonly ROUTE_NAMES: typeof ROUTE_NAMES = ROUTE_NAMES;
  config: WritableSignal<SealConfig> = signal(void 0);
  scheme: Scheme;
  availableLangs: AvailableLangs;
  activeLang: WritableSignal<string> = signal(void 0);
  preference: Signal<Preference> = computed(() => this._userState.preference());
  flagCodes: Record<string, string> = {
    en: 'us',
    kr: 'kr',
    jp: 'jp',
    zh: 'zh'
  };
  flagLabels: Record<string, string> = {
    en: 'English',
    kr: '한국어',
    jp: '日本語',
    zh: '中文'
  };

  get webTheme(): string {
    return this.preference()?.theme ?? localStorage.getItem('webTheme') ?? 'light';
  }

  set webTheme(token: string) {
    localStorage.setItem('webTheme', token);
    this.updatePreference({ theme: token as Scheme });
  }

  get webLanguage(): string {
    return this.preference()?.language ?? localStorage.getItem('webLanguage') ?? 'en';
  }

  set webLanguage(token: string) {
    localStorage.setItem('webLanguage', token);
  }

  constructor(
    private _router: Router,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _profileState: ProfileState,
    private _sealConfigService: SealConfigService,
    private _translocoService: TranslocoService
  ) {}

  public ngOnInit(): void {
    this._sealConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.config.set({ ...config });
    });

    this._sealConfigService.config = { scheme: this.webTheme };

    this.availableLangs = this._translocoService.getAvailableLangs();
    this._translocoService.langChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((activeLang) => {
      this.activeLang.set(activeLang);
    });

    this.setActiveLang(this.webLanguage, false);
  }

  ngOnDestroy(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }

    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  openPanel(): void {
    if (!this._profilePanel || !this._profileOrigin) {
      return;
    }

    if (!this._overlayRef) {
      this._createOverlay();
    }

    this._overlayRef.attach(new TemplatePortal(this._profilePanel, this._viewContainerRef));
  }

  closePanel(): void {
    this._overlayRef.detach();
  }

  signOut(): void {
    // this._websocketService.wsLeaveReminderChannel();
    this._router.navigate(['/', ROUTE_NAMES.SIGN_OUT]).then();
  }

  private _createOverlay(): void {
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: 'seal-backdrop-on-mobile',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._profileOrigin._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom'
          }
        ])
    });

    this._overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._overlayRef.detach();
      });
  }

  setScheme(scheme: Scheme): void {
    this._sealConfigService.config = { scheme };

    this.webTheme = scheme;
  }

  toggleFullscreen() {
    if (!document.fullscreenEnabled) {
      console.log('Fullscreen is not available in this browser.');
      return;
    }
    const fullScreen = document.fullscreenElement;
    if (fullScreen) {
      document.exitFullscreen().then(() => this.fullScreenEnabled.set(false));
    } else {
      document.documentElement
        .requestFullscreen()
        .then(() => this.fullScreenEnabled.set(true))
        .catch(() => {
          console.error('Entering fullscreen mode failed.');
        });
    }
  }

  async setActiveLang(lang: string, savePreference: boolean = true): Promise<void> {
    const module = (this._router.routerState.snapshot.url ?? '/database/company').split('/').filter((w) => !!w)[0];
    await this.translocoController.loadByModule(lang, module);
    this.webLanguage = lang;
    savePreference && this.updatePreference({ language: lang as Language });
    this._translocoService.setActiveLang(lang);
  }

  getLangId(lang: string | LangDefinition): string {
    return typeof lang === 'object' && 'id' in lang ? lang.id : (lang as string);
  }

  updatePreference(body: Partial<Preference>) {
    this._userActions.actions.updatePreference({ body });
  }
}
